import { useEffect, useState, useCallback } from 'react'

export type Theme = 'light' | 'dark'

const STORAGE_KEY = 'theme'
const DEFAULT_THEME: Theme = 'light'

/**
 * F4.1: Shared theme state with localStorage persistence + DOM sync.
 * Replaces 3 duplicated `useState<Theme>` + localStorage reads in
 * Navbar, Settings, and admin/Settings.
 *
 * Default is 'light' (F2 default-theme change).
 */
export function useTheme(): [Theme, (t: Theme | ((prev: Theme) => Theme)) => void, () => void] {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === 'undefined') return DEFAULT_THEME
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved === 'light' || saved === 'dark') return saved
    return DEFAULT_THEME
  })

  // Sync DOM and localStorage whenever theme changes
  useEffect(() => {
    if (typeof document === 'undefined') return
    const root = document.documentElement
    if (theme === 'dark') {
      root.classList.add('dark')
    } else {
      root.classList.remove('dark')
    }
    localStorage.setItem(STORAGE_KEY, theme)
  }, [theme])

  const setTheme = useCallback((t: Theme | ((prev: Theme) => Theme)) => {
    setThemeState(prev => {
      const next = typeof t === 'function' ? t(prev) : t
      return next === 'dark' ? 'dark' : 'light'
    })
  }, [])

  const toggle = useCallback(() => {
    setThemeState(prev => (prev === 'dark' ? 'light' : 'dark'))
  }, [])

  return [theme, setTheme, toggle]
}
